import dayjs from 'dayjs'
import { useTranslation } from 'next-i18next'
import { Box, Flex } from '@mantine/core'
import useLangPrefix from '@/hooks/useLangPrefix'
import { FC, useEffect, useRef, useState } from 'react'
import type { FutureEventItem } from 'api-price'
import { useStore } from '@/store/index'
import { eventTracker } from '@/utils/collectEvent'
import { observer } from 'mobx-react-lite'
import BaseImage from '@/components/BaseImage'
import BaseTooltip from '@/components/BaseTooltip'
import { coinImageUrl, formatTimeCode } from '@/utils/tools'
import Market from './Market'
import { FutureEventItemStyled, TodaySvg } from './style'

const FutureEventLiItem: FC<{ dataItem: FutureEventItem[]; borderState: boolean }> = ({ dataItem, borderState }) => {
  const { t } = useTranslation()
  const { isZh } = useLangPrefix()
  const { isDark } = useStore().globalStore
  const daysOfWeek = [
    t('P.Sunday'),
    t('P.Monday'),
    t('P.Tuesday'),
    t('P.Wednesday'),
    t('P.Thursday'),
    t('P.Friday'),
    t('P.Saturday'),
  ]

  const priceRefs = useRef(null) as any
  const [isTextOverflowing, setIsTextOverflowing] = useState(true)

  useEffect(() => {
    if (priceRefs?.current) {
      const result = priceRefs?.current?.scrollHeight > priceRefs?.current?.clientHeight
      setIsTextOverflowing(result)
    }
  }, [])

  return (
    <FutureEventItemStyled data-border={String(borderState)}>
      <Flex data-name="day" gap={12} align="center" py={16}>
        <TodaySvg day={dayjs(dataItem[0].pub_time * 1000).format('DD')} isDark={isDark} />

        <Flex direction="column" gap={8}>
          <p className="text-3 size-16 font-500">
            {formatTimeCode(new Date().getTime()) === formatTimeCode(dataItem[0].pub_time * 1000)
              ? t('P.Today')
              : daysOfWeek[dayjs(dataItem[0].pub_time * 1000).day()]}
          </p>
          <time className="text-3 size-12 font-400">{formatTimeCode(dataItem[0].pub_time * 1000)}</time>
        </Flex>
      </Flex>
      {dataItem.map(
        ({ logo_url, project_name, symbol, title_zh, title_en, content_text_en, content_text_zh, dimension_24h }) => {
          return (
            <Flex
              key={project_name}
              data-name="event"
              pl={12}
              py={21.5}
              onClick={() => {
                eventTracker('price_sectors_click', {
                  upcoming_url: 'upcoming_url',
                })
              }}
              gap={24}
              align="flex-start"
              direction={{ base: 'column', sm: 'row' }}
            >
              <Flex gap={8} align="center">
                <BaseImage
                  width={32}
                  height={32}
                  style={{ borderRadius: '50%' }}
                  src={coinImageUrl(logo_url, symbol)}
                  coin={symbol}
                />

                <Flex direction="column" gap={8} w={{ base: '100%', sm: 100 }}>
                  {project_name && (
                    <BaseTooltip label={project_name ?? ''}>
                      <span className="text-1 size-16-14 font-700">{project_name}</span>
                    </BaseTooltip>
                  )}

                  <BaseTooltip label={symbol ?? ''}>
                    <span className="text-3 size-12 font-400">{symbol ?? '--'}</span>
                  </BaseTooltip>
                </Flex>
              </Flex>

              <Flex gap={8} direction={'column'} w={'100%'}>
                <Flex justify="space-between" align="center">
                  {(isZh ? (title_zh ?? title_en) : title_en) && (
                    <p className="text-1 size-16-14 font-700 line-120">{isZh ? (title_zh ?? title_en) : title_en}</p>
                  )}

                  <Market coinName={symbol} change={dimension_24h} />
                </Flex>

                <Box
                  component="span"
                  className="text-2 size-16-12 font-400"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    display: '-webkit-box',
                    '-webkit-box-orient': 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    '-webkit-line-clamp': '2',
                    height: isTextOverflowing ? 'calc(1.5em * 2)' : 'auto',
                    lineHeight: '1.5em',
                  }}
                >
                  {isZh ? (content_text_zh ?? content_text_en ?? '--') : (content_text_en ?? '--')}
                </Box>
              </Flex>
            </Flex>
          )
        }
      )}
    </FutureEventItemStyled>
  )
}

export default observer(FutureEventLiItem)
